import { ITrackingDetail } from "@easypost/api";
import { MjmlColumn, MjmlSection, MjmlText } from "@faire/mjml-react";
import { TrackerDataType } from "@redotech/redo-model/advanced-flow/type-system/data-types/trackable-types";
import {
  Section,
  SharedProps,
  getButtonSize,
} from "@redotech/redo-model/email-builder";
import { ExternalShipmentTracker } from "@redotech/redo-model/trackable";
import { memo } from "react";
import { getDateString } from "../../date-utils";
import { prettyTrackerStatus } from "../../settings-elements/order-tracking/delivery-estimation";

export type TrackingInfoState = Section.TrackingInfo & SharedProps;

interface DeliveryDateBasic {
  hasBeenDelivered: boolean;
}

interface EstimatedDeliveryDate extends DeliveryDateBasic {
  hasBeenDelivered: false;
  estimatedDeliveryDate: Date | null;
}

interface HasBeenDeliveredDate extends DeliveryDateBasic {
  hasBeenDelivered: true;
  deliveryDate: Date;
}

type DeliveryDate = EstimatedDeliveryDate | HasBeenDeliveredDate;

const TrackingEventDisplay = ({
  message,
  datetime,
  tracking_location,
  complete,
  status,
  backgroundColor,
  outlineColor,
  dotColor,
  last,
  isBuilder = false,
}: {
  message?: string;
  datetime?: Date;
  tracking_location?: ITrackingDetail["tracking_location"];
  complete: boolean;
  status?: ExternalShipmentTracker["status"];
  backgroundColor: string;
  outlineColor: string;
  dotColor: string;
  last?: boolean;
  isBuilder?: boolean;
}) => {
  return (
    <tr>
      <th
        style={{
          verticalAlign: "top",
          textAlign: "center",
          padding: "0 8px",
          position: "relative",
        }}
      >
        <div
          style={{
            height: "8px",
            width: "8px",
            borderRadius: "50%",
            backgroundColor: "transparent",
            border: `2px solid ${outlineColor}`,
            padding: "2px",
          }}
        >
          {complete && (
            <div
              style={{
                backgroundColor: dotColor,
                borderRadius: "50%",
                width: "100%",
                height: "100%",
              }}
            />
          )}
        </div>
        {!last && (
          <div
            style={{
              height: "34px",
              marginTop: "5px",
              marginLeft: "7px",
              borderLeft: `2px solid ${outlineColor}`,
            }}
          />
        )}
      </th>
      <th style={{ verticalAlign: "top", lineHeight: "1" }}>
        <div style={{ fontWeight: "bold" }}>{message}</div>
        <div
          style={{
            padding: "6px 0 22px 0",
            opacity: "70%",
            fontSize: ".8rem",
            // minHeight: "45px", TODO: fix
          }}
        >
          {datetime && (
            <div style={{ paddingBottom: "6px" }}>
              {getDateString(datetime)}
            </div>
          )}
        </div>
      </th>
    </tr>
  );
};

const createDeliveryDate = (
  tracker: TrackerDataType["Tracker"],
): DeliveryDate => {
  const hasBeenDelivered = tracker.details?.some(
    (detail) => detail.status === "delivered",
  );

  const estDate = tracker.deliveryEstimate
    ? new Date(tracker.deliveryEstimate)
    : null;

  const detail = tracker.details?.find(
    (detail) => detail.status === "delivered",
  );
  const potentialDeliveredDate = detail?.datetime
    ? new Date(detail.datetime)
    : new Date();

  return hasBeenDelivered
    ? { hasBeenDelivered: true, deliveryDate: potentialDeliveredDate }
    : { hasBeenDelivered: false, estimatedDeliveryDate: estDate };
};

export const EmailTrackingInfo = memo(function emailTrackingInfo(
  props: TrackingInfoState,
) {
  if (!props.schemaFieldName) throw new Error("schemaFieldName is required");
  const tracker = props.schemaInstance[
    props.schemaFieldName
  ] as TrackerDataType["Tracker"];
  const hasShipped = tracker.details?.some(
    (detail) => detail.status === "in_transit",
  );

  const deliveryDate = createDeliveryDate(tracker);

  const trackingDetails: {
    message: string;
    status: ExternalShipmentTracker["status"];
    datetime: Date;
  }[] = [];

  trackingDetails.push({
    message: `${tracker.trackableType.titleCase} received`,
    status: "pre_transit",
    datetime: tracker.orderCreatedAt,
  });

  if (hasShipped) {
    const detail = tracker.details?.find(
      (detail) => detail.status === "in_transit",
    );
    const datetime = detail?.datetime ?? new Date();
    trackingDetails.push({
      message: "Shipped",
      status: "in_transit",
      datetime: datetime,
    });
  }

  if (deliveryDate.hasBeenDelivered) {
    trackingDetails.push({
      message: "Delivered",
      status: "delivered",
      datetime: deliveryDate.deliveryDate,
    });
  }

  const buttonSize = getButtonSize(
    props.buttonSize,
    props.buttonWidth,
    props.buttonHeight,
  );

  const padding = props.sectionPadding;

  // TODO: rewrite in MJML
  return (
    <MjmlSection
      backgroundColor={props.sectionColor || "#ffffff"}
      paddingBottom={padding.bottom}
      paddingLeft={padding.left}
      paddingRight={padding.right}
      paddingTop={padding.top}
      textAlign="center"
    >
      <MjmlColumn>
        <MjmlText color={props.textColor} fontFamily={props.fontFamily}>
          <table style={{ textAlign: "left", width: "100%" }}>
            <thead>
              {/* TODO: red text when delayed (but delayed is not a tracked status? */}
              <tr>
                <th
                  colSpan={2}
                  style={{
                    fontSize: "2rem",
                    fontWeight: "bold",
                    color: props.textColor,
                    paddingBottom: "16px",
                  }}
                >
                  {tracker.status
                    ? prettyTrackerStatus[tracker.status]
                    : "Tracking Info"}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ fontWeight: "bold", verticalAlign: "top" }}>
                  {!deliveryDate.hasBeenDelivered && (
                    <div>Estimated delivery:</div>
                  )}
                  {!deliveryDate.hasBeenDelivered
                    ? DeliveryCard(deliveryDate.estimatedDeliveryDate)
                    : DeliveryCard(deliveryDate.deliveryDate)}
                </td>
                <td style={{ maxWidth: "140px" }}>
                  <table>
                    <tbody>
                      {trackingDetails.map((detail, i) => (
                        <TrackingEventDisplay
                          backgroundColor={props.sectionColor}
                          complete
                          dotColor={
                            props.statusIndicatorInnerColor ?? "#000000"
                          }
                          isBuilder={props.isBuilder}
                          key={i}
                          last={
                            detail === trackingDetails.slice(-1)[0] &&
                            deliveryDate.hasBeenDelivered
                          }
                          outlineColor={props.statusIndicatorColor ?? "#000000"}
                          {...detail}
                        />
                      ))}
                      {!hasShipped && (
                        <TrackingEventDisplay
                          backgroundColor={props.sectionColor}
                          complete={false}
                          dotColor={
                            props.statusIndicatorInnerColor ?? "#000000"
                          }
                          isBuilder={props.isBuilder}
                          message="Shipped"
                          outlineColor={props.statusIndicatorColor ?? "#000000"}
                          status="in_transit"
                        />
                      )}
                      {!deliveryDate.hasBeenDelivered && (
                        <TrackingEventDisplay
                          backgroundColor={props.sectionColor}
                          complete={false}
                          dotColor={
                            props.statusIndicatorInnerColor ?? "#000000"
                          }
                          isBuilder={props.isBuilder}
                          last
                          message="Delivered"
                          outlineColor={props.statusIndicatorColor ?? "#000000"}
                          status="delivered"
                        />
                      )}
                    </tbody>
                  </table>
                </td>
              </tr>
              <tr>
                <td colSpan={2}>
                  {props.buttonVisible && (
                    <a
                      href={
                        props.isBuilder
                          ? undefined
                          : props.utm.applyToUrl(tracker.link)
                      }
                      style={{
                        color: props.buttonTextColor ?? "#ffffff",
                        backgroundColor: props.buttonColor,
                        borderRadius: `${props.buttonCornerRadius ?? 8}px`,
                        height: `${buttonSize.height}px`,
                        width: `${buttonSize.width}px`,
                        display: "block",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        lineHeight: `${buttonSize.height}px`,
                        textAlign: "center",
                        fontWeight: "bold",
                        textDecoration: "none",
                      }}
                    >
                      Track your package
                    </a>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </MjmlText>
      </MjmlColumn>
    </MjmlSection>
  );
});

const DeliveryCard = (date_: Date | null) => {
  // this line isn't supposed to be necessary but sometimes date_ seems to not actually be a date
  const date = date_
    ? "toLocaleDateString" in date_
      ? date_
      : new Date(date_)
    : null;

  if (date && isNaN(date.getTime())) {
    throw new Error(
      `Invalid date, got type: ${typeof date_}, value: "${date_}"`,
    );
  }

  return (
    <>
      <div
        style={{ fontSize: "1.25rem", paddingTop: "8px", lineHeight: "1.25" }}
      >
        {!date && "Awaiting ETA"}
        {date?.toLocaleDateString("en-US", {
          timeZone: "UTC",
          weekday: "long",
        })}
        {date && ","}
        <br />
        {date?.toLocaleDateString("en-US", { timeZone: "UTC", month: "long" })}
      </div>
      <div style={{ fontSize: "4rem" }}>
        {date?.toLocaleDateString("en-US", { timeZone: "UTC", day: "numeric" })}
      </div>
    </>
  );
};
